import { FC } from 'react';

import useAuthenticatedAction from 'common/hooks/useAuthenticatedAction';
import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';

import PinCode from '../../components/PinCode';
import { useCreateInternationalTransfer } from './hooks/useCreateInternationalTransfer';

export const InternationalTransferConfirmation: FC<
  StepProps<BankTransferCreationStep>
> = ({ goBack, goNext }) => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferConfirmation',
    );
  }
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferConfirmation',
    );
  }

  const currentLanguage = useCurrentLanguage();

  const {
    bankTransfer: {
      additionalInformation: requirements,
      fromBankAccountId: bankAccountId,
      quote: { internationalTransferQuoteId },
      receipt: { fileRef: receiptFileRef },
      recipient: { id: internationalTransferRecipientId },
    },
  } = useBankTransferContext('internationalTransferConfirmation');

  const { createInternationalTransfer, isCreatingTransfer } =
    useCreateInternationalTransfer();
  const { isLoading: isAuthenticating, onPasscodeSubmit } =
    useAuthenticatedAction({
      onPasscodeSuccess: async (token) => {
        try {
          const { errors } = await createInternationalTransfer({
            context: {
              headers: {
                'short-lived-token': token,
              },
            },
            variables: {
              input: {
                bankAccountId,
                companyProfileId,
                companyUserId,
                internationalTransferQuoteId,
                internationalTransferRecipientId,
                lang: currentLanguage,
                receiptFileRef,
                requirements,
              },
            },
          });
          goNext({
            key: errors?.[0] ? 'failure' : 'internationalTransferSuccess',
          });
        } catch (error) {
          goNext({ key: 'failure' });
        }
      },
      scope: Scope.BANK_TRANSFERS_WRITE,
    });

  const loading = isAuthenticating || isCreatingTransfer;
  return (
    <>
      <TrackView pageName="InternationalTransferConfirmation" />
      <PinCode
        goBack={goBack}
        loading={loading}
        onPasscodeSubmit={onPasscodeSubmit}
      />
    </>
  );
};
